import React, { useCallback, useState } from 'react';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  TextField,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { BUTTON_COLOR, BUTTON_VARIANT } from 'src/components/Button/types';
import { useWorkerActions } from 'src/hooks/useWorkerActions';
import { Button } from 'src/components/Button';
import { Avatar } from 'src/components/Avatar';
import {
  AVATAR_BG_COLOR,
  AVATAR_SHADOW,
  AVATAR_SIZE,
  AVATAR_VARIANT,
} from 'src/components/Avatar/types';
import { Box } from 'src/components/Box';
import { getActionSubText, getJobOptions, handleOpenWorkerProfile } from 'src/utils/workerUtils';
import { JobApplicationStatus, JobApplicationAction } from 'src/DTO/Job.type';
import { ActionType, WorkerActionsDialogProps } from './types';
import { useStoreActions } from 'src/stores';

export const WorkerActionsDialog = ({
  isOpen,
  handleClose,
  worker,
  showProfileButton = true,
}: WorkerActionsDialogProps) => {
  const [selectedId, setSelectedId] = useState<string>('');
  const [options, setOptions] = useState<{ label: string; id: string }[]>([]);
  const [action, setAction] = useState<ActionType>(null);

  const { t } = useTranslation();
  const {
    resendWorkerInvite,
    terminateWorker,
    removeWorkerFromCompany,
    handleJobApplication,
    isLoading,
  } = useWorkerActions();

  const openConfirmModal = useStoreActions(actions => actions.context.openConfirmDialog);

  const handleConfirm = useCallback(() => {
    if (!worker) return;

    if (action === 'acceptApp') {
      handleJobApplication({
        variables: { applicationId: selectedId, action: JobApplicationAction.ACCEPT },
      });
    } else if (action === 'declineApp') {
      handleJobApplication({
        variables: { applicationId: selectedId, action: JobApplicationAction.DECLINE },
      });
    } else if (action === 'terminate') {
      terminateWorker({ variables: { workerId: worker.id, jobId: selectedId } });
    }
  }, [action, selectedId, worker]);

  const reset = () => {
    setSelectedId('');
    setOptions([]);
    setAction(null);
  };

  const lastImageUrl = !!worker?.imageUrls.length
    ? worker.imageUrls[worker.imageUrls.length - 1]
    : undefined;

  const pendingJobApplications = worker?.applications?.filter(
    app => app.status === JobApplicationStatus.Pending || app.status === JobApplicationStatus.Sent,
  );
  const activeJobs = worker?.jobs;

  if (!worker) return null;

  return (
    <Dialog open={isOpen} onClose={() => handleClose()}>
      <Stack spacing={2} sx={{ px: 5, py: 1 }}>
        <DialogTitle
          sx={{ px: 0, pb: 1, borderBottom: '1px solid black', borderColor: 'divider', mb: 2 }}
          variant="h5">
          {worker.name}
        </DialogTitle>

        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <Avatar
            alt={worker.name}
            bgColor={AVATAR_BG_COLOR.DARK}
            src={lastImageUrl}
            variant={AVATAR_VARIANT.ROUNDED}
            size={AVATAR_SIZE.XXL}
            shadow={AVATAR_SHADOW.SM}
          />
          {showProfileButton && (
            <Button
              variant={BUTTON_VARIANT.GRADIENT}
              color={BUTTON_COLOR.INFO}
              onClick={() => handleOpenWorkerProfile(worker)}>
              <Trans>workerCard.profile</Trans>
            </Button>
          )}
        </Box>

        <Divider />

        {!activeJobs?.length && !pendingJobApplications?.length && (
          <>
            <Button
              variant={BUTTON_VARIANT.GRADIENT}
              color={BUTTON_COLOR.INFO}
              onClick={() => resendWorkerInvite({ variables: { workerId: worker.id } })}>
              <Trans>workersPage.actions.resendInvite.title</Trans>
            </Button>
            <Button
              variant={BUTTON_VARIANT.GRADIENT}
              color={BUTTON_COLOR.ERROR}
              onClick={() => {
                openConfirmModal({
                  title: t('workersPage.actions.removeWorkerFromCompany.confirm.title'),
                  body: !!worker?.offers?.length ? (
                    <DialogContentText>
                      {t('workersPage.actions.removeWorkerFromCompany.confirm.bodyText', {
                        offerCount: worker.offers.length,
                      })}
                    </DialogContentText>
                  ) : null,
                  onConfirm: () => removeWorkerFromCompany({ variables: { workerId: worker.id } }),
                });
              }}>
              <Trans>workersPage.actions.removeWorkerFromCompany.title</Trans>
            </Button>
          </>
        )}

        {!!pendingJobApplications?.length && (
          <>
            <DialogContentText>
              <Trans
                i18nKey="workersPage.actions.handleApplication.applicationsCount"
                values={{ count: pendingJobApplications?.length }}
              />
            </DialogContentText>
            <Button
              variant={BUTTON_VARIANT.GRADIENT}
              color={BUTTON_COLOR.INFO}
              onClick={() => {
                setAction('acceptApp');
                setOptions(getJobOptions(pendingJobApplications));
              }}>
              <Trans>workersPage.actions.handleApplication.title.accept</Trans>
            </Button>
            <Button
              variant={BUTTON_VARIANT.GRADIENT}
              color={BUTTON_COLOR.ERROR}
              onClick={() => {
                setAction('declineApp');
                setOptions(getJobOptions(pendingJobApplications));
              }}>
              <Trans>workersPage.actions.handleApplication.title.decline</Trans>
            </Button>
          </>
        )}

        {!!activeJobs?.length && (
          <>
            <DialogContentText>
              <Trans
                i18nKey="workersPage.actions.terminateWorker.activeJobsCount"
                values={{ count: activeJobs?.length }}
              />
            </DialogContentText>
            <Button
              variant={BUTTON_VARIANT.GRADIENT}
              color={BUTTON_COLOR.ERROR}
              onClick={() => {
                setAction('terminate');
                setOptions(getJobOptions(activeJobs));
              }}>
              <Trans>workersPage.actions.terminateWorker.title</Trans>
            </Button>
          </>
        )}
      </Stack>

      <DialogActions>
        <Button onClick={handleClose}>
          <Trans>generic.cancel</Trans>
        </Button>
      </DialogActions>

      <Dialog open={!!options.length} onClose={reset} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Trans>workersScreen.chooseJob</Trans>
        </DialogTitle>

        <DialogContent>
          <DialogContentText mb={2}>
            <Trans>{getActionSubText(action, worker.name, t)}</Trans>
          </DialogContentText>

          <Autocomplete
            onChange={(_, option) => setSelectedId(option?.id || '')}
            options={options || []}
            disabled={isLoading}
            getOptionDisabled={option => option.id === selectedId}
            getOptionLabel={option => option.label || ''}
            value={options?.find(option => option.id === selectedId) || null}
            renderInput={params => (
              <TextField {...params} placeholder={t('workersScreen.chooseJob')} />
            )}
          />
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button disabled={isLoading} onClick={reset}>
            <Trans>generic.cancel</Trans>
          </Button>
          <Button
            disabled={!selectedId || isLoading}
            variant={BUTTON_VARIANT.GRADIENT}
            color={BUTTON_COLOR.DARK}
            onClick={() => handleConfirm()}>
            <Trans>generic.OK</Trans>
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
